"use client";

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from "react";

type CacheEntry = {
    fromCache: boolean;
    isOffline: boolean;
};

type CacheStatusContextValue = {
    status: CacheEntry;
    report: (key: string, fromCache: boolean, isOffline: boolean) => void;
};

const defaultValue: CacheStatusContextValue = {
    status: { fromCache: false, isOffline: false },
    report: () => {},
};

const CacheStatusContext = createContext<CacheStatusContextValue>(defaultValue);

export function CacheStatusProvider({ children }: { children: ReactNode }) {
    const [entries, setEntries] = useState<Map<string, CacheEntry>>(new Map());

    const report = useCallback((key: string, fromCache: boolean, isOffline: boolean) => {
        setEntries((prev) => {
            const next = new Map(prev);
            if (!fromCache && !isOffline) {
                next.delete(key);
            } else {
                next.set(key, { fromCache, isOffline });
            }
            return next;
        });
    }, []);

    const status = useMemo(() => {
        let fromCache = false;
        let isOffline = false;
        for (const entry of entries.values()) {
            if (entry.fromCache) fromCache = true;
            if (entry.isOffline) isOffline = true;
        }
        return { fromCache, isOffline };
    }, [entries]);

    return (
        <CacheStatusContext.Provider value={{ status, report }}>
            {children}
        </CacheStatusContext.Provider>
    );
}

export function useCacheStatus() {
    return useContext(CacheStatusContext).status;
}

export function useCacheStatusReporter() {
    return useContext(CacheStatusContext).report;
}
